import { oneLine } from 'common-tags';

import { Container, RichInline } from '@/atoms';
import { CardSlider, ImageCard, Prose } from '@/molecules';

import { isBotonContacto, useBotonContacto } from '@/hooks/useBotonContacto';
import {
  blockSpacing,
  eyebrowClass,
  sectionBgProps,
  textStyleClass,
} from '@/utils';
import { getRealUrl } from '@/utils/utils';

/** Header + grid 1/2/4 de `ImageCard`. Cada tarjeta admite máscara `Forma` → ImageShape. */
export const ComponentRevisadasTarjetasConImagen = ({
  Eyebrow,
  Titulo,
  Descripcion,
  EyebrowEstilo,
  TituloEstilo,
  DescripcionEstilo,
  TarjetasTituloEstilo,
  TarjetasDescripcionEstilo,
  TarjetasEtiquetaEstilo,
  TarjetasBotonVariante,
  TarjetasBotonSize,
  Tarjetas,
  Ancla,
  FondoSeccion,
  Espaciado,
  // Toggle Strapi opt-in: en móvil pasa de 1 col a 2 col (mantiene
  // sm/lg como están). Default null/false → comportamiento histórico
  // intacto para tenants que no lo piquen.
  MobileCompact,
  MobileSlider,
  // Toggle Strapi opt-in: tarjetas con la escala tipográfica compacta del
  // Figma HoM (título 24/32, cuerpo 16/24, texto oscuro). Default
  // null/false → look histórico intacto.
  Compacto,
}: any) => {
  const { modal, abrirContacto } = useBotonContacto();
  if (!Tarjetas?.length) return null;
  // Columnas en desktop = nº de tarjetas, tope 4. Evita la columna vacía
  // cuando hay 3 (o menos): 3 tarjetas → 3 columnas a ancho completo
  // (como el Figma), en vez de 3 metidas en una rejilla de 4. Con 4+ se
  // queda en 4 → comportamiento histórico intacto.
  const lgCols = Math.min(Tarjetas.length, 4);
  const eyebrowCls = eyebrowClass(EyebrowEstilo);
  const tituloCls = textStyleClass(TituloEstilo);
  const descripcionCls = textStyleClass(DescripcionEstilo);
  // Defaults a nivel de bloque — pick per-card gana.
  const tarjetasTituloCls = textStyleClass(TarjetasTituloEstilo);
  const tarjetasDescripcionCls = textStyleClass(TarjetasDescripcionEstilo);
  const tarjetasEtiquetaCls = textStyleClass(TarjetasEtiquetaEstilo);

  return (
    <Container
      wrapper
      classNameWrapper={blockSpacing(Espaciado)}
      id={Ancla}
      {...sectionBgProps(FondoSeccion)}
    >
      {(Eyebrow || Titulo || Descripcion) && (
        <div
          className={`flex max-w-full flex-col gap-2 ${
            Descripcion ? 'mb-12 lg:mb-20' : 'mb-8 lg:mb-10'
          }`}
        >
          {(Eyebrow || Titulo) && (
            <div className="flex flex-col items-end self-start">
              {Eyebrow && (
                <RichInline
                  html={Eyebrow}
                  className={oneLine`font-accent text-primary-500 ${
                    eyebrowCls ?? 'text-3xl leading-10 font-normal'
                  }`}
                />
              )}
              {Titulo && (
                <RichInline
                  as="h2"
                  html={Titulo}
                  className={oneLine`text-headline text-text-primary font-primary ${
                    tituloCls ?? 'text-headline-h1 font-bold'
                  }`}
                />
              )}
            </div>
          )}
          {Descripcion && (
            <Prose
              className={oneLine`text-body text-text-primary ${
                descripcionCls ?? '[&_p]:text-body-l'
              }`}
              html={Descripcion}
            />
          )}
        </div>
      )}

      {(() => {
        const cards = Tarjetas.map((tarjeta: any, index: number) => {
          const formaSvgUrl = tarjeta.Forma?.Svg?.url
            ? getRealUrl(tarjeta.Forma.Svg.url)
            : undefined;
          const imagen = tarjeta.Imagen
            ? {
                src: getRealUrl(tarjeta.Imagen.url),
                alt: tarjeta.Imagen.alternativeText ?? tarjeta.Titulo,
              }
            : undefined;
          const imgPos =
            Number.isFinite(tarjeta.PosicionImagen?.X) &&
            Number.isFinite(tarjeta.PosicionImagen?.Y)
              ? `${tarjeta.PosicionImagen.X}% ${tarjeta.PosicionImagen.Y}%`
              : undefined;
          const button = tarjeta.Boton?.Texto
            ? {
                label: tarjeta.Boton.Texto,
                href: tarjeta.Boton.Link || '#',
                variant: tarjeta.Boton.Variante ?? TarjetasBotonVariante,
                size: tarjeta.Boton.Size ?? TarjetasBotonSize,
                ...(isBotonContacto(tarjeta.Boton) && {
                  onClick: () => abrirContacto(tarjeta.Boton),
                }),
              }
            : undefined;

          return (
            <li
              key={tarjeta.id ?? index}
              className={oneLine`${
                MobileSlider
                  ? 'w-[80vw] shrink-0 snap-center sm:h-full sm:w-auto'
                  : 'h-full'
              }`}
            >
              <ImageCard
                image={imagen}
                imageShapeUrl={formaSvgUrl}
                imagePosition={imgPos}
                label={tarjeta.Etiqueta}
                title={tarjeta.Titulo}
                description={tarjeta.Descripcion}
                compact={Compacto}
                titleStyleClass={
                  textStyleClass(tarjeta.TituloEstilo) ?? tarjetasTituloCls
                }
                descriptionStyleClass={
                  textStyleClass(tarjeta.DescripcionEstilo) ??
                  tarjetasDescripcionCls
                }
                labelStyleClass={
                  textStyleClass(tarjeta.EtiquetaEstilo) ?? tarjetasEtiquetaCls
                }
                button={button}
              />
            </li>
          );
        });

        const gridClass = `grid ${MobileCompact ? 'grid-cols-2 gap-4' : 'grid-cols-1 gap-8'} sm:grid-cols-2 sm:gap-8 lg:grid-cols-${lgCols} lg:gap-5`;

        return MobileSlider ? (
          <CardSlider
            className={oneLine`scrollbar-hide -mx-4 flex snap-x snap-mandatory items-stretch gap-3 overflow-x-auto px-[10vw] pb-1 sm:mx-0 sm:grid sm:snap-none sm:grid-cols-2 sm:gap-8 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-${lgCols} lg:gap-5`}
          >
            {cards}
          </CardSlider>
        ) : (
          <ul className={gridClass}>{cards}</ul>
        );
      })()}
      {modal}
    </Container>
  );
};

ComponentRevisadasTarjetasConImagen.displayName =
  'ComponentRevisadasTarjetasConImagen';
